import React, { useState } from 'react';
import { Icons } from '../components/Icons';
import { db, appId } from '../utils/firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import AddDataModal from '../components/AddDataModal';
import DetailModal from '../components/DetailModal';
import { safeHtml } from '../utils/safeHtml';

const AnnouncementsView = ({ data, isAdmin, showToast }) => {
    const [editing, setEditing] = useState(null);
    const [selected, setSelected] = useState(null);
    const [isAdding, setIsAdding] = useState(false);

    const announcements = data?.announcements || [];
    const sortedData = [...announcements].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

    const handleSave = async (formData) => {
        try {
            const coll = collection(db, 'artifacts', appId, 'public', 'data', 'announcements');
            if (formData.id) await updateDoc(doc(coll, formData.id), formData);
            else await addDoc(coll, { ...formData, timestamp: serverTimestamp() });
            setEditing(null); setIsAdding(false);
            showToast("Update Published", "success");
        } catch (err) { showToast("Firebase Error", "error"); }
    };

    const handleDelete = async (e, id) => { 
        e.stopPropagation();
        if (!window.confirm("Delete this update permanently?")) return;
        try { await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'announcements', id)); showToast("Purged.", "success"); } 
        catch (e) { showToast("Database Error", "error"); } 
    };

    return (
        <div className="flex-1 overflow-y-auto p-6 md:p-10 bg-[#050505] custom-scrollbar fade-in h-full pb-24">
            <style>{`
                .rich-text-preview a { color: #3b82f6; text-decoration: underline; }
                .rich-text-preview blockquote { border-left: 4px solid #333; padding-left: 1rem; color: #aaa; font-style: italic; margin: 1rem 0; }
                .rich-text-preview pre { background: #111; padding: 1rem; border-radius: 4px; font-family: monospace; overflow-x: auto; }
                .rich-text-preview ul { list-style-type: disc; padding-left: 1.5rem; margin: 0.5rem 0; }
                .rich-text-preview ol { list-style-type: decimal; padding-left: 1.5rem; margin: 0.5rem 0; }
                .rich-text-preview h1, .rich-text-preview h2, .rich-text-preview h3 { font-weight: 900; margin-top: 1rem; margin-bottom: 0.5rem; color: white; }
            `}</style>
            
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8 border-b border-gray-900 pb-4 shrink-0 max-w-4xl mx-auto">
                <div>
                    <h2 className="text-xl font-bold flex items-center gap-2 text-white"><Icons.MessageSquare className="text-blue-500" /> PUBLIC UPDATES BLOG</h2>
                    <p className="text-[10px] text-gray-500 font-mono mt-1 tracking-widest uppercase">Posts displayed on the Public Landing Page</p>
                </div>
                {isAdmin && (
                    <button onClick={() => setIsAdding(true)} className="bg-blue-900/20 text-blue-400 border border-blue-900/50 hover:bg-blue-900 hover:text-white px-6 py-3 rounded text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 shrink-0 transition-colors">
                        <Icons.Plus size={14} /> NEW POST
                    </button>
                )}
            </div>

            <div className="max-w-4xl mx-auto flex flex-col gap-8">
                {sortedData.length === 0 ? (
                    <div className="text-center text-gray-600 font-mono text-sm mt-10 border border-dashed border-gray-800 p-10">NO UPDATES PUBLISHED.</div>
                ) : (
                    sortedData.map(item => (
                        <div key={item.id} onClick={() => setSelected(item)} className="bg-[#0a0a0a] border border-gray-800 p-6 md:p-8 rounded shadow-lg hover:border-blue-500/50 transition-all cursor-pointer flex flex-col relative overflow-hidden group">
                            
                            <div className="flex justify-between items-start border-b border-gray-900 pb-3 mb-4 shrink-0">
                                <div className="flex gap-2 items-center flex-wrap">
                                    <span className={`border px-2 py-0.5 rounded text-[8px] font-bold tracking-widest uppercase ${item.visibility === 'INTERNAL' ? 'bg-red-900/20 text-red-400 border-red-900/50' : 'bg-green-900/20 text-green-400 border-green-900/50'}`}>
                                        {item.visibility === 'INTERNAL' ? 'INTERNAL' : 'PUBLIC'}
                                    </span>
                                    <span className="bg-blue-900/20 text-blue-400 border border-blue-900/50 px-2 py-0.5 rounded text-[8px] font-bold tracking-widest uppercase">
                                        {item.category || 'NEWS'}
                                    </span>
                                    <span className="bg-gray-900 text-gray-400 border border-gray-800 px-2 py-0.5 rounded text-[8px] font-bold tracking-widest uppercase">
                                        {item.author || 'CRI'}
                                    </span>
                                </div>
                                <div className="text-[10px] text-gray-500 font-mono ml-2 shrink-0">{item.date}</div>
                            </div>
                            
                            <div className="flex-1 flex flex-col">
                                <h3 className="font-bold text-white text-xl leading-tight mb-3">{item.title}</h3>
                                <div className="text-sm text-gray-300 font-sans leading-relaxed rich-text-preview" dangerouslySetInnerHTML={{ __html: safeHtml(item.text) }} />
                            </div>
                            
                            {((item.links && item.links.length > 0) || item.url) && (
                                <div className="mt-4 pt-4 border-t border-gray-900">
                                    <span className="text-[10px] text-cyan-500 font-bold uppercase tracking-widest flex items-center gap-1">
                                        <Icons.ExternalLink size={12}/> Links Attached
                                    </span>
                                </div>
                            )}

                            {isAdmin && (
                                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2 bg-black/80 p-1 rounded backdrop-blur">
                                    <button onClick={(e) => { e.stopPropagation(); setEditing(item); }} className="text-gray-400 hover:text-white p-1.5 rounded transition-colors"><Icons.Edit2 size={14}/></button>
                                    <button onClick={(e) => handleDelete(e, item.id)} className="text-gray-400 hover:text-red-500 p-1.5 rounded transition-colors"><Icons.Trash2 size={14}/></button>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            {selected && DetailModal && (
                <DetailModal item={selected} type="PUBLIC ANNOUNCEMENT" isAdmin={isAdmin} onClose={() => setSelected(null)} onEdit={(item) => { setSelected(null); setTimeout(() => setEditing(item), 100); }} />
            )}

            {(isAdding || editing) && AddDataModal && (
                <AddDataModal type="announcement" initialData={editing} onClose={() => { setIsAdding(false); setEditing(null); }} onSubmit={handleSave} showToast={showToast} isAdmin={isAdmin} />
            )}
        </div>
    );
};

export default AnnouncementsView;